/**
 * Gedeelde toegankelijke client-side categorie-filter.
 *
 * Eén implementatie voor de jobs-, knowledge-base- en landing-pages-base-blokken
 * (voorheen drie identieke kopieën — alleen de BEM-prefix verschilde). Elk blok
 * roept initFilter() aan met z'n eigen selectors/classes; de blok-specifieke
 * markup + styling blijven bij de blokken.
 *
 * De filters zijn een groep TOGGLE-buttons (aria-pressed), GEEN ARIA-tablist: de
 * cards zijn geen tabpanelen. Verbergen gaat via het hidden-attribuut zodat
 * verborgen cards echt uit de toegankelijkheidsboom verdwijnen (+ een
 * blok-specifieke --hidden class als styling-haak). Alles binnen `root` gescoped;
 * no-op wanneer er minder dan twee filter-knoppen zijn.
 *
 * @typedef {Object} FilterConfig
 * @property {string} block       - Sectie-selector (bv. '.jobs').
 * @property {string} filter      - Filter-knop-selector (bv. '.jobs__filter').
 * @property {string} card        - Card-selector (bv. '.jobs__card').
 * @property {string} hiddenClass - Verberg-class zonder punt (bv. 'jobs__card--hidden').
 */

/**
 * Initialiseert één filter-instance.
 *
 * @param {HTMLElement} root - De sectie-root.
 * @param {FilterConfig} config - De blok-configuratie.
 */
function initInstance(root, config) {
    const filters = Array.from(root.querySelectorAll(config.filter));
    if (filters.length < 2) {
        return;
    }

    const cards = Array.from(root.querySelectorAll(config.card));

    // Pas een filter toe: markeer de actieve knop (aria-pressed) en toon/verberg de
    // cards. "all" toont alles; een term-slug toont alleen cards waarvan data-cat die
    // slug bevat (meerdere categorieën → match op elk ervan).
    const apply = (button) => {
        const value = button.getAttribute('data-filter');

        filters.forEach((item) => {
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });

        cards.forEach((card) => {
            const cats = (card.getAttribute('data-cat') || '').split(/\s+/).filter(Boolean);
            const match = value === 'all' || cats.includes(value);
            card.hidden = !match;
            card.classList.toggle(config.hiddenClass, !match);
        });
    };

    filters.forEach((button) => {
        button.addEventListener('click', () => apply(button));
    });
}

/**
 * Initialiseert alle filter-instances van één blok binnen een scope.
 *
 * @param {FilterConfig} config - De blok-configuratie.
 * @param {ParentNode} [scope] - Zoekwortel (default: document).
 */
export function initFilter(config, scope = document) {
    scope.querySelectorAll(config.block).forEach((root) => initInstance(root, config));
}
